import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../index.css';

const HallOfFame = () => {
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchHallOfFame();
  }, []);

  const fetchHallOfFame = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hall-of-fame');
      setMembers(response.data.data);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching hall of fame:', error);
      setError('Failed to load Hall of Fame data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryMembers = (categoryKey) => {
    if (categoryKey === 'all') return members;
    return members.filter(member => member.categories.includes(categoryKey));
  };

  const getCategoryBadgeStyle = (categoryKey) => {
    const colors = {
      union_amplifiers: { bg: '#FF6B6B', text: '#FFF' },
      giveaway_legends: { bg: '#4ECDC4', text: '#FFF' },
      vision_shapers: { bg: '#FFD93D', text: '#000' },
      growth_architects: { bg: '#95E1D3', text: '#000' },
      union_backers_bronze: { bg: '#CD7F32', text: '#FFF' },
      union_backers_silver: { bg: '#C0C0C0', text: '#000' },
      union_backers_gold: { bg: '#FFD700', text: '#000' },
      union_backers_platinum: { bg: '#E5E4E2', text: '#000' }
    };
    
    const color = colors[categoryKey] || { bg: '#6C757D', text: '#FFF' };
    
    return {
      backgroundColor: color.bg,
      color: color.text,
      padding: '6px 16px',
      borderRadius: '25px',
      fontSize: '0.9rem',
      fontWeight: 'bold',
      display: 'inline-block',
      margin: '4px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
    };
  };

  const renderMemberCard = (member) => {
    const user = member.user;
    const card = member.communityCard;
    
    return (
      <div
        key={member._id}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          borderRadius: '20px',
          padding: '2rem',
          position: 'relative',
          border: member.featured ? '3px solid #FFD700' : '2px solid rgba(255,255,255,0.15)',
          boxShadow: member.featured 
            ? '0 10px 40px rgba(255, 215, 0, 0.3)' 
            : '0 8px 32px rgba(0,0,0,0.3)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer',
          backdropFilter: 'blur(10px)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px)';
          e.currentTarget.style.boxShadow = member.featured 
            ? '0 15px 50px rgba(255, 215, 0, 0.4)' 
            : '0 12px 40px rgba(0,0,0,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = member.featured 
            ? '0 10px 40px rgba(255, 215, 0, 0.3)' 
            : '0 8px 32px rgba(0,0,0,0.3)';
        }}
        onClick={() => navigate(`/profile/${user.username}`)}
      >
        {member.featured && (
          <div style={{
            position: 'absolute',
            top: '-15px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#000',
            padding: '8px 20px',
            borderRadius: '25px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(255, 215, 0, 0.5)',
            zIndex: 10
          }}>
            ⭐ FEATURED LEGEND ⭐
          </div>
        )}

        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'start' }}>
          {/* Avatar */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '4px solid rgba(255,255,255,0.2)',
            flexShrink: 0,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            {(card?.avatar || user.avatar?.url) ? (
              <img
                src={card?.avatar || user.avatar?.url}
                alt={user.username}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#FFF'
              }}>
                {user.username[0].toUpperCase()}
              </div>
            )}
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.8rem',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {user.username}
              </h3>
              {user.accountType !== 'regular' && (
                <span style={{
                  background: 'rgba(100, 200, 255, 0.2)',
                  color: '#64C8FF',
                  padding: '4px 12px',
                  borderRadius: '15px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  border: '1px solid rgba(100, 200, 255, 0.3)'
                }}>
                  {user.accountType.replace('_', ' ').toUpperCase()}
                </span>
              )}
            </div>

            {/* Categories */}
            <div style={{ marginBottom: '1rem' }}>
              {member.categories.map(cat => (
                <span key={cat} style={getCategoryBadgeStyle(cat)}>
                  {categories[cat]?.icon} {categories[cat]?.name}
                </span>
              ))}
            </div>

            {/* About */}
            {card?.about && (
              <p style={{
                margin: '1rem 0',
                fontSize: '0.95rem',
                lineHeight: '1.6',
                color: 'rgba(255,255,255,0.9)',
                padding: '1rem',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '12px',
                borderLeft: '4px solid rgba(102, 126, 234, 0.5)'
              }}>
                {card.about}
              </p>
            )}

            {/* Social Links */}
            {card?.socialLinks && card.socialLinks.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {card.socialLinks.slice(0, 6).map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        color: '#FFF',
                        padding: '8px 14px',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      🔗 {link.platform}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '10px',
              display: 'flex',
              gap: '1.5rem',
              fontSize: '0.9rem'
            }}>
              <span>👥 {user.followerCount || 0} Followers</span>
              <span style={{ opacity: 0.6 }}>
                Inducted {new Date(member.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <div style={{ fontSize: '1.5rem' }}>Loading Hall of Fame...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        color: '#ff4444'
      }}>
        <div style={{ fontSize: '1.5rem' }}>{error}</div>
      </div>
    );
  }

  const categoryGroups = [
    { key: 'all', name: 'All Legends', icon: '🏆' },
    ...Object.entries(categories).map(([key, cat]) => ({
      key,
      name: cat.name,
      icon: cat.icon
    }))
  ];

  const displayMembers = getCategoryMembers(activeCategory);

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '4rem 2rem',
        borderRadius: '25px',
        marginBottom: '3rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 15px 50px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.1
        }}></div>
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{
            margin: '0 0 1rem 0',
            fontSize: '3.5rem',
            fontWeight: 'bold',
            color: '#FFF',
            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
            letterSpacing: '2px'
          }}>
            🏆 HALL OF FAME 🏆
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.3rem',
            color: 'rgba(255,255,255,0.95)',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.6'
          }}>
            Celebrating the legendary members who shaped GamersUnion through their 
            dedication, creativity, generosity, and passion for gaming
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '2rem',
        justifyContent: 'center'
      }}>
        {categoryGroups.map(cat => {
          const count = cat.key === 'all' ? members.length : getCategoryMembers(cat.key).length;
          const isActive = activeCategory === cat.key;
          
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              style={{
                background: isActive 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'rgba(255,255,255,0.1)',
                color: '#FFF',
                border: isActive ? 'none' : '2px solid rgba(255,255,255,0.2)',
                padding: '12px 24px',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
                boxShadow: isActive ? '0 6px 20px rgba(102, 126, 234, 0.4)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {cat.icon} {cat.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Members Grid */}
      {displayMembers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '20px',
          border: '2px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎭</div>
          <p style={{ fontSize: '1.3rem', opacity: 0.7 }}>
            No legends in this category yet
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '2rem',
          gridTemplateColumns: 'repeat(auto-fill, minmax(550px, 1fr))'
        }}>
          {displayMembers
            .sort((a, b) => {
              if (a.featured && !b.featured) return -1;
              if (!a.featured && b.featured) return 1;
              return 0;
            })
            .map(renderMemberCard)}
        </div>
      )}

      {/* Footer Note */}
      <div style={{
        marginTop: '4rem',
        padding: '2rem',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '15px',
        textAlign: 'center',
        border: '2px solid rgba(255,255,255,0.1)'
      }}>
        <p style={{ margin: 0, fontSize: '1.1rem', opacity: 0.8 }}>
          💜 Thank you to all our legends for making GamersUnion the amazing community it is! 💜
        </p>
      </div>
    </div>
  );
};

export default HallOfFame;
